import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Popconfirm,
  message,
  Row,
  Col,
  Statistic,
  Typography
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ShopOutlined,
  UserOutlined,
  CarOutlined,
  DollarOutlined
} from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

interface Franchise {
  id: string;
  name: string;
  ownerName: string;
  phone: string;
  email: string;
  address: string;
  type: 'rental' | 'ride' | 'both';
  status: 'active' | 'inactive' | 'pending';
  vehicleCount: number;
  driverCount: number;
  monthlyRevenue: number;
  createdAt: string;
}

const FranchiseManagement: React.FC = () => {
  const [franchises, setFranchises] = useState<Franchise[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFranchise, setEditingFranchise] = useState<Franchise | null>(null);
  const [form] = Form.useForm();

  // サンプルデータ
  useEffect(() => {
    setFranchises([
      {
        id: '1',
        name: '東京レンタカーサービス',
        ownerName: '田中太郎',
        phone: '03-1234-5678',
        email: 'tanaka@example.com',
        address: '東京都渋谷区渋谷一丁目1-1',
        type: 'rental',
        status: 'active',
        vehicleCount: 25,
        driverCount: 15,
        monthlyRevenue: 15000000,
        createdAt: '2023-01-15'
      },
      {
        id: '2',
        name: 'スピードタクシー',
        ownerName: '佐藤花子',
        phone: '06-9876-5432',
        email: 'sato@example.com',
        address: '大阪市北区大深二丁目2-2',
        type: 'ride',
        status: 'active',
        vehicleCount: 40,
        driverCount: 35,
        monthlyRevenue: 28000000,
        createdAt: '2023-02-20'
      },
      {
        id: '3',
        name: '総合モビリティサービス',
        ownerName: '山田一郎',
        phone: '052-5555-7777',
        email: 'yamada@example.com',
        address: '愛知県名古屋市中区三丁目3-3',
        type: 'both',
        status: 'pending',
        vehicleCount: 60,
        driverCount: 45,
        monthlyRevenue: 42000000,
        createdAt: '2023-03-10'
      }
    ]);
  }, []);

  const typeColors = {
    rental: 'blue',
    ride: 'green',
    both: 'purple'
  };

  const typeLabels = {
    rental: 'レンタカー',
    ride: 'タクシー',
    both: '総合サービス'
  };

  const statusColors = {
    active: 'success',
    inactive: 'default',
    pending: 'warning'
  };

  const statusLabels = {
    active: '運営中',
    inactive: '休止中',
    pending: '審査中'
  };

  const columns = [
    {
      title: 'フランチャイズ店名',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <strong>{text}</strong>
    },
    {
      title: '代表者',
      dataIndex: 'ownerName',
      key: 'ownerName'
    },
    {
      title: '電話番号',
      dataIndex: 'phone',
      key: 'phone'
    },
    {
      title: 'サービス内容',
      dataIndex: 'type',
      key: 'type',
      render: (type: keyof typeof typeLabels) => (
        <Tag color={typeColors[type]}>{typeLabels[type]}</Tag>
      )
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      render: (status: keyof typeof statusLabels) => (
        <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>
      )
    },
    {
      title: '車両台数',
      dataIndex: 'vehicleCount',
      key: 'vehicleCount'
    },
    {
      title: '運転手数',
      dataIndex: 'driverCount',
      key: 'driverCount'
    },
    {
      title: '月間売上',
      dataIndex: 'monthlyRevenue',
      key: 'monthlyRevenue',
      render: (revenue: number) => `¥${revenue.toLocaleString()}`
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: Franchise) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            編集
          </Button>
          <Popconfirm
            title="このフランチャイズ店を削除しますか？"
            onConfirm={() => handleDelete(record.id)}
            okText="確定"
            cancelText="キャンセル"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              削除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const handleAdd = () => {
    setEditingFranchise(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (franchise: Franchise) => {
    setEditingFranchise(franchise);
    form.setFieldsValue(franchise);
    setModalVisible(true);
  };

  const handleDelete = (id: string) => {
    setFranchises(prev => prev.filter(item => item.id !== id));
    message.success('削除しました');
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingFranchise) {
        // 編集
        setFranchises(prev => prev.map(item => 
          item.id === editingFranchise.id 
            ? { ...item, ...values }
            : item
        ));
        message.success('更新しました');
      } else {
        // 新規追加
        const newFranchise: Franchise = {
          id: Date.now().toString(),
          ...values,
          vehicleCount: 0,
          driverCount: 0,
          monthlyRevenue: 0,
          createdAt: new Date().toISOString().split('T')[0]
        };
        setFranchises(prev => [newFranchise, ...prev]);
        message.success('追加しました');
      }
      
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('フォーム検証に失敗しました:', error);
    }
  };

  const totalStats = {
    totalFranchises: franchises.length,
    activeFranchises: franchises.filter(f => f.status === 'active').length,
    totalVehicles: franchises.reduce((sum, f) => sum + f.vehicleCount, 0),
    totalRevenue: franchises.reduce((sum, f) => sum + f.monthlyRevenue, 0)
  };

  return (
    <div>
      <Title level={2}>フランチャイズ店管理</Title>
      
      {/* 統計カード */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="総フランチャイズ店数"
              value={totalStats.totalFranchises}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="運営中フランチャイズ店"
              value={totalStats.activeFranchises}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="総車両台数"
              value={totalStats.totalVehicles}
              prefix={<CarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="月間総売上"
              value={totalStats.totalRevenue}
              prefix={<DollarOutlined />}
              formatter={(value) => `¥${Number(value).toLocaleString()}`}
            />
          </Card>
        </Col>
      </Row>

      {/* フランチャイズ店リスト */}
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            フランチャイズ店追加
          </Button>
        </div>
        
        <Table
          columns={columns}
          dataSource={franchises}
          loading={loading}
          rowKey="id"
          pagination={{
            total: franchises.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]}件 / 全${total}件`
          }}
        />
      </Card>

      {/* 追加/編集モーダル */}
      <Modal
        title={editingFranchise ? 'フランチャイズ店編集' : 'フランチャイズ店追加'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ status: 'pending', type: 'both' }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="フランチャイズ店名"
                rules={[{ required: true, message: 'フランチャイズ店名を入力してください' }]}
              >
                <Input placeholder="フランチャイズ店名を入力" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="ownerName"
                label="代表者氏名"
                rules={[{ required: true, message: '代表者氏名を入力してください' }]}
              >
                <Input placeholder="代表者氏名を入力" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="電話番号"
                rules={[
                  { required: true, message: '電話番号を入力してください' },
                  { pattern: /^0\d{1,4}-\d{1,4}-\d{4}$/, message: '正しい電話番号形式で入力してください' }
                ]}
              >
                <Input placeholder="電話番号を入力" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="メールアドレス"
                rules={[
                  { required: true, message: 'メールアドレスを入力してください' },
                  { type: 'email', message: '正しいメールアドレス形式で入力してください' }
                ]}
              >
                <Input placeholder="メールアドレスを入力" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="address"
            label="住所"
            rules={[{ required: true, message: '住所を入力してください' }]}
          >
            <Input.TextArea placeholder="詳細住所を入力" rows={2} />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="サービス内容"
                rules={[{ required: true, message: 'サービス内容を選択してください' }]}
              >
                <Select placeholder="サービス内容を選択">
                  <Option value="rental">レンタカー</Option>
                  <Option value="ride">タクシー</Option>
                  <Option value="both">総合サービス</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="ステータス"
                rules={[{ required: true, message: 'ステータスを選択してください' }]}
              >
                <Select placeholder="ステータスを選択">
                  <Option value="pending">審査中</Option>
                  <Option value="active">運営中</Option>
                  <Option value="inactive">休止中</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default FranchiseManagement;